import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, RotateCcw, Volume2, VolumeX, SkipForward, CheckCircle, XCircle, ChevronLeft, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import SimliAvatar from './SimliAvatar';
import AnimatedAvatar from './AnimatedAvatar';
import api from '../lib/api';

// ULTRA-INTELLIGENT Presentation with Simli Lip-Synced Avatar
export default function WhiteboardPresentation({ slides, onComplete, quiz, onModuleStarted }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [visibleItems, setVisibleItems] = useState(0);
  const [presentationComplete, setPresentationComplete] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(true); // Auto-play next slide
  
  // Simli session state
  const [simliSession, setSimliSession] = useState(null);
  const [simliLoading, setSimliLoading] = useState(false);
  const [simliError, setSimliError] = useState(null);
  const [simliConnectionState, setSimliConnectionState] = useState('idle');
  const [useSimli, setUseSimli] = useState(true); // Toggle between Simli and fallback
  
  const slideAudioRef = useRef(null);
  const simliAvatarRef = useRef(null); // Ref to SimliAvatar for sending audio
  const isTransitioningRef = useRef(false);
  const hasStartedRef = useRef(false);

  const slide = slides?.[currentSlide];
  const totalSlides = slides?.length || 0;
  const bulletPoints = slide?.bulletPoints || [];
  const images = slide?.images || [];

  // Initialize Simli session
  const sessionRequestedRef = useRef(false);
  
  useEffect(() => {
    const initSimliSession = async () => {
      // Prevent duplicate requests (React strict mode protection)
      if (simliSession || simliLoading || !useSimli || sessionRequestedRef.current) return;
      
      sessionRequestedRef.current = true;
      setSimliLoading(true);
      setSimliError(null);
      
      try {
        const response = await api.post('/simli/session', {
          face_id: "5514e24d-6086-46a3-ace4-6a7264e5cb7c",
          handle_silence: true,
          max_session_length: 3600,
          max_idle_time: 600,
          sync_audio: true
        });
        
        if (response.data.session_token) {
          setSimliSession(response.data);
          console.log('Simli session initialized:', response.data);
        } else {
          throw new Error('No session token received');
        }
      } catch (err) {
        console.error('Failed to initialize Simli session:', err);
        setSimliError(err.response?.data?.detail || err.message || 'Failed to initialize avatar');
        setUseSimli(false);
        sessionRequestedRef.current = false; // Allow retry on actual error
      } finally {
        setSimliLoading(false);
      }
    };
    
    initSimliSession();
  }, [useSimli, simliSession, simliLoading]);

  // Handle Simli connection state changes
  const handleSimliConnectionChange = useCallback((state) => {
    setSimliConnectionState(state);
    // Only fallback on actual failure, not on disconnect (which might be temporary)
    if (state === 'failed') {
      console.log('Simli connection failed, falling back to standard audio');
      setUseSimli(false);
    }
  }, []);

  // Animate content items based on audio progress
  useEffect(() => {
    if (isPlaying && bulletPoints.length > 0) {
      const totalItems = bulletPoints.length;
      const itemsToShow = Math.min(
        Math.ceil((audioProgress + 0.1) * totalItems),
        totalItems
      );
      setVisibleItems(itemsToShow);
    }
  }, [audioProgress, isPlaying, bulletPoints.length]);

  // Handle audio time update
  const handleTimeUpdate = useCallback(() => {
    const audio = slideAudioRef.current;
    if (audio && audio.duration) {
      const progress = audio.currentTime / audio.duration;
      setAudioProgress(progress);
      
      // Log progress occasionally
      if (Math.floor(audio.currentTime) % 10 === 0 && audio.currentTime > 0) {
        console.log(`Audio progress: ${Math.round(progress * 100)}% (${Math.round(audio.currentTime)}s / ${Math.round(audio.duration)}s)`);
      }
    }
  }, []);

  // Auto-advance to next slide and start playing
  const autoAdvanceToNextSlide = useCallback(async () => {
    if (currentSlide < totalSlides - 1) {
      // Stop current Simli audio
      if (simliAvatarRef.current?.stopAudio) {
        simliAvatarRef.current.stopAudio();
      }
      
      // Move to next slide
      const nextSlide = currentSlide + 1;
      setCurrentSlide(nextSlide);
      setVisibleItems(0);
      setAudioProgress(0);
      
      // Auto-play the next slide after a brief transition
      setTimeout(async () => {
        const nextSlideData = slides?.[nextSlide];
        if (nextSlideData?.audioBase64) {
          const audio = slideAudioRef.current;
          if (audio) {
            audio.src = `data:audio/mp3;base64,${nextSlideData.audioBase64}`;
            
            // Check if Simli is truly connected
            const simliConnected = useSimli && 
                                   simliAvatarRef.current?.isConnected?.() && 
                                   simliConnectionState === 'connected';
            
            if (simliConnected) {
              audio.muted = true;
              try {
                await audio.play();
                await simliAvatarRef.current.sendAudioForLipSync(nextSlideData.audioBase64);
              } catch (e) {
                console.error('Auto-play with Simli failed:', e);
              }
            } else {
              // Fallback: play audio normally
              console.log('Auto-advancing with fallback audio');
              audio.muted = isMuted;
              try {
                await audio.play();
              } catch (e) {
                console.error('Auto-play failed:', e);
              }
            }
          }
        } else {
          // No audio, just show all content
          setVisibleItems(slides?.[nextSlide]?.bulletPoints?.length || 0);
        }
      }, 500); // Brief delay for smooth transition
    } else {
      // Last slide finished - presentation complete
      setPresentationComplete(true);
      setIsPlaying(false);
    }
  }, [currentSlide, totalSlides, slides, useSimli, isMuted, simliConnectionState]);

  // Handle audio ended
  const handleAudioEnded = useCallback(() => {
    console.log('Audio ended event triggered, slide:', currentSlide, 'total:', totalSlides);
    if (isTransitioningRef.current) return;
    
    setVisibleItems(bulletPoints.length);
    setAudioProgress(1);
    
    // Auto-advance after showing all content for a moment
    if (autoPlayEnabled) {
      console.log('Auto-advancing to next slide...');
      setTimeout(() => {
        autoAdvanceToNextSlide();
      }, 1500); // 1.5 second delay to let user see the final content
    } else {
      // Manual mode - stop at end of slide
      if (currentSlide >= totalSlides - 1) {
        setPresentationComplete(true);
        setIsPlaying(false);
      }
    }
  }, [currentSlide, totalSlides, bulletPoints.length, autoPlayEnabled, autoAdvanceToNextSlide]);

  // Setup audio event listeners
  useEffect(() => {
    const audio = slideAudioRef.current;
    if (!audio) return;
    
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleAudioEnded);
    
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleAudioEnded);
    };
  }, [handleTimeUpdate, handleAudioEnded]);

  // Reset state when slide changes
  useEffect(() => {
    setLoadedImages({});
    setVisibleItems(0);
    setAudioProgress(0);
  }, [currentSlide]);

  // Notify parent when module starts playing (for pre-generation)
  const notifyModuleStarted = useCallback(() => {
    if (onModuleStarted && !hasStartedRef.current) {
      onModuleStarted();
    }
  }, [onModuleStarted]);

  const startPresentation = async () => {
    if (!slide?.audioBase64) {
      setIsPlaying(true);
      setVisibleItems(bulletPoints.length);
      notifyModuleStarted();
      return;
    }

    hasStartedRef.current = true;
    setIsPlaying(true);
    setVisibleItems(0);
    
    // Notify parent to pre-generate next module
    notifyModuleStarted();
    
    const audio = slideAudioRef.current;
    if (audio) {
      // Check if Simli is truly connected and stable
      const simliConnected = useSimli && 
                             simliAvatarRef.current?.isConnected?.() && 
                             simliConnectionState === 'connected';
      
      // Check if we're resuming from pause
      const isResuming = simliConnected && simliAvatarRef.current?.isPaused?.();
      
      if (isResuming) {
        console.log('Resuming from pause...');
        // Resume both HTML audio and Simli
        audio.muted = true;
        await audio.play();
        simliAvatarRef.current.resumeAudio();
      } else {
        // Starting fresh
        audio.src = `data:audio/mp3;base64,${slide.audioBase64}`;
        
        try {
          if (simliConnected) {
            console.log('Sending audio to Simli for lip-sync...');
            // Mute original audio - Simli will output the audio with lip-sync
            audio.muted = true;
            
            // Play original audio (muted) for progress tracking, send to Simli for lip-sync
            const playPromise = audio.play();
            const simliPromise = simliAvatarRef.current.sendAudioForLipSync(slide.audioBase64);
            
            await Promise.all([playPromise, simliPromise]);
          } else {
            // Simli not connected - play audio normally through original element
            console.log('Playing audio without Simli (fallback mode)');
            audio.muted = isMuted;
            await audio.play();
          }
        } catch (e) {
          console.error('Audio play failed:', e);
          // Fallback: show all content immediately
          setVisibleItems(bulletPoints.length);
        }
      }
    }
  };

  const pausePresentation = () => {
    setIsPlaying(false);
    slideAudioRef.current?.pause();
    
    // Pause Simli audio streaming (keeps position for resume)
    const simliConnected = useSimli && simliAvatarRef.current?.isConnected?.();
    if (simliConnected && simliAvatarRef.current?.pauseAudio) {
      simliAvatarRef.current.pauseAudio();
    }
  };

  const goToNextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      isTransitioningRef.current = true;
      slideAudioRef.current?.pause();
      // Stop Simli audio streaming completely (not pause, since we're changing slides)
      if (simliAvatarRef.current?.stopAudio) {
        simliAvatarRef.current.stopAudio();
      }
      setCurrentSlide(prev => prev + 1);
      setVisibleItems(0);
      setAudioProgress(0);
      setIsPlaying(false);
      setTimeout(() => {
        isTransitioningRef.current = false;
      }, 100);
    }
  };

  const goToPrevSlide = () => {
    if (currentSlide > 0) {
      isTransitioningRef.current = true;
      slideAudioRef.current?.pause();
      // Stop Simli audio streaming
      if (simliAvatarRef.current?.stopAudio) {
        simliAvatarRef.current.stopAudio();
      }
      setCurrentSlide(prev => prev - 1);
      setVisibleItems(0);
      setAudioProgress(0);
      setIsPlaying(false);
      setTimeout(() => {
        isTransitioningRef.current = false;
      }, 100);
    }
  };

  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    
    // If Simli is handling audio, the mute state is passed to SimliAvatar component
    // which will mute its audio output. The original audio is already muted when Simli is active.
    // For non-Simli playback, mute the original audio element
    const simliConnected = useSimli && simliAvatarRef.current?.isConnected?.();
    if (!simliConnected && slideAudioRef.current) {
      slideAudioRef.current.muted = newMuted;
    }
  };

  const restartPresentation = () => {
    // Stop Simli audio streaming
    if (simliAvatarRef.current?.stopAudio) {
      simliAvatarRef.current.stopAudio();
    }
    setCurrentSlide(0);
    setVisibleItems(0);
    setAudioProgress(0);
    setPresentationComplete(false);
    setIsPlaying(false);
    setShowQuiz(false);
    setQuizAnswers({});
    setQuizSubmitted(false);
    hasStartedRef.current = false;
  };

  const handleQuizAnswer = (questionIdx, optionIdx) => {
    if (!quizSubmitted) {
      setQuizAnswers(prev => ({ ...prev, [questionIdx]: optionIdx }));
    }
  };

  const submitQuiz = () => {
    if (!quiz?.questions) return;
    
    let correct = 0;
    quiz.questions.forEach((q, idx) => {
      if (quizAnswers[idx] === q.correctAnswer) correct++;
    });
    
    const score = Math.round((correct / quiz.questions.length) * 100);
    setQuizScore(score);
    setQuizSubmitted(true);
    
    if (score >= 70 && onComplete) {
      onComplete(true);
    }
  };

  const handleImageLoad = (idx) => {
    setLoadedImages(prev => ({ ...prev, [idx]: true }));
  };

  const handleImageError = (idx) => {
    setLoadedImages(prev => ({ ...prev, [idx]: 'error' }));
  };

  // Quiz View
  if (showQuiz && quiz?.questions) {
    return (
      <div className="bg-slate-900 rounded-xl p-8" data-testid="quiz-section">
        <h2 className="text-2xl font-bold text-white mb-6">{quiz.title || 'Knowledge Check'}</h2>
        
        <div className="space-y-6">
          {quiz.questions.map((q, qIdx) => (
            <div key={qIdx} className="bg-slate-800 rounded-lg p-5">
              <p className="text-white font-medium mb-4">{qIdx + 1}. {q.question}</p>
              <div className="grid grid-cols-1 gap-2">
                {q.options.map((opt, oIdx) => {
                  const isSelected = quizAnswers[qIdx] === oIdx;
                  const isCorrect = quizSubmitted && q.correctAnswer === oIdx;
                  const isWrong = quizSubmitted && isSelected && !isCorrect;
                  
                  return (
                    <button
                      key={oIdx}
                      onClick={() => handleQuizAnswer(qIdx, oIdx)}
                      disabled={quizSubmitted}
                      className={`p-3 rounded-lg text-left transition-all flex items-center gap-3 ${
                        isCorrect ? 'bg-green-600 text-white' :
                        isWrong ? 'bg-red-600 text-white' :
                        isSelected ? 'bg-[#0056D2] text-white' :
                        'bg-slate-700 text-slate-200 hover:bg-slate-600'
                      }`}
                    >
                      {quizSubmitted && isCorrect && <CheckCircle className="w-5 h-5" />}
                      {quizSubmitted && isWrong && <XCircle className="w-5 h-5" />}
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {!quizSubmitted ? (
          <Button 
            onClick={submitQuiz} 
            className="mt-6 bg-[#0056D2] hover:bg-[#0046b2]"
            disabled={Object.keys(quizAnswers).length < quiz.questions.length}
          >
            Submit Quiz
          </Button>
        ) : (
          <div className="mt-6 p-4 rounded-lg bg-slate-800">
            <p className={`text-xl font-bold ${quizScore >= 70 ? 'text-green-400' : 'text-red-400'}`}>
              Score: {quizScore}%
            </p>
            <p className="text-slate-300 mt-2">
              {quizScore >= 70 ? '🎉 Congratulations! Module completed!' : 'Keep learning and try again!'}
            </p>
            <Button onClick={restartPresentation} className="mt-4" variant="outline">
              <RotateCcw className="w-4 h-4 mr-2" /> Review Lesson
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Presentation Complete View
  if (presentationComplete) {
    return (
      <div className="bg-slate-900 rounded-xl p-8 text-center" data-testid="presentation-complete">
        <div className="w-20 h-20 mx-auto mb-6 bg-green-500 rounded-full flex items-center justify-center">
          <CheckCircle className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-4">Lesson Complete!</h2>
        <p className="text-slate-300 mb-6">Great job! Ready for the knowledge check?</p>
        <div className="flex justify-center gap-4">
          <Button onClick={restartPresentation} variant="outline">
            <RotateCcw className="w-4 h-4 mr-2" /> Review
          </Button>
          {quiz?.questions && (
            <Button onClick={() => setShowQuiz(true)} className="bg-[#0056D2] hover:bg-[#0046b2]">
              Take Quiz
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!slides || slides.length === 0) {
    return (
      <div className="bg-slate-900 rounded-xl p-8 text-center">
        <p className="text-slate-400">No presentation content available.</p>
      </div>
    );
  }

  const hasPairedContent = images.length > 0 && images.length === bulletPoints.length;

  return (
    <div className="presentation-container relative" data-testid="whiteboard-presentation">
      {/* Hidden audio element */}
      <audio ref={slideAudioRef} preload="auto" crossOrigin="anonymous" />

      {/* Main Presentation */}
      <div className="bg-slate-900 rounded-xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0056D2] to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-bold text-xl line-clamp-1 flex-1 mr-4">
              {slide?.title}
            </h3>
            <div className="flex items-center gap-3">
              <span className="bg-white/20 text-white px-4 py-1.5 rounded-full text-sm font-medium">
                Slide {currentSlide + 1} of {totalSlides}
              </span>
              {isPlaying && (
                <span className="flex items-center gap-1.5 bg-red-500 text-white px-3 py-1.5 rounded-full text-xs font-medium animate-pulse">
                  <span className="w-2 h-2 bg-white rounded-full"></span>
                  LIVE
                </span>
              )}
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-3 h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white rounded-full transition-all duration-300" 
              style={{ width: `${((currentSlide + audioProgress) / totalSlides) * 100}%` }}
            />
          </div>
        </div>

        {/* Slide Content with Simli Avatar */}
        <div className="flex min-h-[550px]">
          {/* Left: Simli Avatar Section */}
          <div className="w-80 bg-gradient-to-b from-slate-800 via-slate-850 to-slate-900 border-r border-slate-700 flex flex-col items-center justify-center p-6 relative overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2220%22 height=%2220%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Ccircle cx=%221%22 cy=%221%22 r=%221%22 fill=%22white%22 fill-opacity=%220.03%22/%3E%3C/svg%3E')]" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#0056D2]/5 via-transparent to-indigo-600/5" />
            
            {/* Avatar container */}
            <div className="relative z-10">
              {/* Simli loading state */}
              {simliLoading && (
                <div className="flex flex-col items-center justify-center h-[300px] w-[300px]">
                  <Loader2 className="w-10 h-10 text-[#0056D2] animate-spin mb-3" />
                  <p className="text-white text-sm">Initializing AI Avatar...</p>
                  <p className="text-slate-400 text-xs mt-1">Powered by Simli</p>
                </div>
              )}
              
              {/* Simli Avatar with ref for audio lip-sync */}
              {!simliLoading && useSimli && simliSession && (
                <SimliAvatar 
                  ref={simliAvatarRef}
                  isPlaying={isPlaying} 
                  isMuted={isMuted}
                  sessionToken={simliSession.session_token}
                  iceServers={simliSession.ice_servers}
                  faceId={simliSession.face_id}
                  onConnectionChange={handleSimliConnectionChange}
                />
              )}
              
              {/* Fallback Avatar */}
              {!simliLoading && !useSimli && (
                <div className="relative">
                  <AnimatedAvatar 
                    isPlaying={isPlaying} 
                    isMuted={isMuted}
                    audioRef={slideAudioRef}
                  />
                  {simliError && (
                    <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-amber-500/20 border border-amber-500/40 rounded-lg px-3 py-1.5 text-xs text-amber-400 whitespace-nowrap">
                      <AlertCircle className="w-3 h-3" />
                      <span>Using fallback avatar</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: Content Area */}
          <div className="flex-1 p-6 bg-gradient-to-br from-slate-800 to-slate-900 overflow-y-auto">
            {hasPairedContent ? (
              // Paired Image + Bullet Points
              <div className="space-y-4">
                {bulletPoints.map((point, idx) => {
                  const image = images[idx];
                  const isVisible = idx < visibleItems;
                  const imageLoaded = loadedImages[idx] === true;
                  const imageError = loadedImages[idx] === 'error';
                  
                  return (
                    <div 
                      key={idx}
                      className={`flex items-stretch gap-4 bg-slate-800/70 rounded-xl overflow-hidden transition-all duration-700 transform border border-slate-700/50 ${
                        isVisible 
                          ? 'opacity-100 translate-x-0 scale-100' 
                          : 'opacity-0 -translate-x-8 scale-95'
                      }`}
                      style={{ transitionDelay: `${idx * 250}ms` }}
                    >
                      {/* Number Badge */}
                      <div className={`w-12 flex items-center justify-center transition-colors ${
                        isVisible ? 'bg-[#0056D2]' : 'bg-slate-700'
                      }`}>
                        <span className="text-white font-bold text-xl">{idx + 1}</span>
                      </div>

                      {/* Image */}
                      <div className="w-36 h-28 flex-shrink-0 relative bg-slate-700 my-2 rounded-lg overflow-hidden">
                        {!imageLoaded && !imageError && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                          </div>
                        )}
                        {image?.url && (
                          <img 
                            src={image.url}
                            alt={image.caption || point}
                            className={`w-full h-full object-cover transition-all duration-500 ${
                              imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-110'
                            }`}
                            onLoad={() => handleImageLoad(idx)}
                            onError={() => handleImageError(idx)}
                          />
                        )}
                        {imageError && (
                          <div className="absolute inset-0 flex items-center justify-center bg-slate-700">
                            <span className="text-2xl">📊</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 py-3 pr-4 flex flex-col justify-center">
                        <p className="text-white text-base leading-relaxed">{point}</p>
                        {image?.caption && (
                          <p className="text-[#0056D2] text-xs mt-1.5 font-medium">{image.caption}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              // Fallback layout
              <div className="grid gap-4">
                {bulletPoints.map((point, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center gap-4 bg-slate-800/50 rounded-lg p-4 transition-all duration-500 ${
                      idx < visibleItems ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                    }`}
                    style={{ transitionDelay: `${idx * 150}ms` }}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      idx < visibleItems ? 'bg-[#0056D2]' : 'bg-slate-700'
                    }`}>
                      <span className="text-white font-bold">{idx + 1}</span>
                    </div>
                    <p className="text-white text-lg">{point}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="bg-slate-800 px-6 py-4 border-t border-slate-700">
          <div className="flex items-center justify-between">
            {/* Navigation */}
            <div className="flex items-center gap-2">
              <Button
                onClick={goToPrevSlide}
                disabled={currentSlide === 0}
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              {/* Slide dots */}
              <div className="flex gap-1.5">
                {slides.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      slideAudioRef.current?.pause();
                      setCurrentSlide(idx);
                      setIsPlaying(false);
                      setVisibleItems(0);
                    }}
                    className={`h-2.5 rounded-full transition-all ${
                      idx === currentSlide 
                        ? 'bg-[#0056D2] w-6' 
                        : idx < currentSlide 
                          ? 'bg-green-500 w-2.5' 
                          : 'bg-slate-600 w-2.5'
                    }`}
                  />
                ))}
              </div>
              
              <Button
                onClick={goToNextSlide}
                disabled={currentSlide === totalSlides - 1}
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Playback controls */}
            <div className="flex items-center gap-3">
              <Button
                onClick={toggleMute}
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white"
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </Button>
              
              {!isPlaying ? (
                <Button 
                  onClick={startPresentation}
                  className="bg-[#0056D2] hover:bg-[#0046b2] px-6"
                >
                  <Play className="w-4 h-4 mr-2" /> Start Lesson
                </Button>
              ) : (
                <Button 
                  onClick={pausePresentation}
                  variant="outline"
                  className="border-slate-600"
                >
                  <Pause className="w-4 h-4 mr-2" /> Pause
                </Button>
              )}
              
              {currentSlide === totalSlides - 1 && visibleItems === bulletPoints.length && (
                <Button 
                  onClick={() => setPresentationComplete(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" /> Complete
                </Button>
              )}
            </div>

            {/* Skip to quiz */}
            {quiz?.questions && (
              <Button
                onClick={() => {
                  setPresentationComplete(true);
                  setShowQuiz(true);
                }}
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-white"
              >
                <SkipForward className="w-4 h-4 mr-1" /> Skip to Quiz
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
